import { useState, FormEvent } from "react";

const propertyTypes = ["Residential", "Commercial", "Industrial", "Multi-Family"];

export default function CTAForm() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem("message") as HTMLTextAreaElement)?.value || "";
    if (message.length > 500) return;
    setLoading(true);
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => { data.append(key, value.toString()); });
    try {
      await fetch("https://readdy.ai/api/form/d77ukor86jhav3jpevc0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/1ec14b81c865cee9e03fefe77a7c33a3.jpg"
          alt="East Bay neighborhood aerial view"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-stone-950/95 via-stone-950/88 to-amber-950/85"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col lg:flex-row gap-14 lg:gap-20 items-start">

          {/* Left: Copy */}
          <div className="w-full lg:w-[46%] pt-2">
            <span className="text-amber-400 text-xs font-bold tracking-[0.2em] uppercase">Get Started Today</span>
            <h2
              className="text-4xl md:text-5xl lg:text-[52px] font-black text-white mt-4 mb-6 leading-tight tracking-tight"
              style={{ fontFamily: "'Syne', sans-serif" }}
            >
              Your System Should<br />Work Harder.<br />
              <span className="text-amber-400">Let&apos;s Make It.</span>
            </h2>
            <p className="text-white/60 text-base leading-relaxed mb-10 max-w-sm">
              Whether your solar is underperforming, you need battery backup, or your electrical panel needs an upgrade — get a free, no-obligation estimate from a licensed expert.
            </p>

            <div className="space-y-4 mb-10">
              {[
                { icon: "ri-tools-line", text: "Solar repair & system diagnostics" },
                { icon: "ri-battery-charge-line", text: "Battery storage for backup & independence" },
                { icon: "ri-flashlight-line", text: "Panel upgrades & electrical services" },
                { icon: "ri-charging-pile-2-line", text: "EV charger installation" },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-amber-500/15 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-amber-400 text-sm`}></i>
                  </div>
                  <span className="text-white/70 text-sm">{item.text}</span>
                </div>
              ))}
            </div>

            <a
              href="tel:9258723408"
              className="inline-flex items-center gap-2.5 text-white/80 hover:text-white text-sm font-medium transition-colors cursor-pointer"
            >
              <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg">
                <i className="ri-phone-fill text-amber-400"></i>
              </div>
              Prefer to call? <span className="text-amber-400 font-semibold">(925) 872-3408</span>
            </a>
          </div>

          {/* Right: Form */}
          <div className="w-full lg:flex-1">
            <div className="bg-white rounded-2xl p-7 md:p-9">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 flex items-center justify-center bg-amber-50 rounded-full mx-auto mb-5">
                    <i className="ri-checkbox-circle-fill text-amber-500 text-3xl"></i>
                  </div>
                  <h3 className="text-xl font-black text-gray-900 mb-2" style={{ fontFamily: "'Syne', sans-serif" }}>
                    Request Received!
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed max-w-xs mx-auto">
                    We&apos;ll be in touch within 1 business day to schedule your free consultation.
                  </p>
                </div>
              ) : (
                <>
                  <div className="mb-7">
                    <h3 className="text-xl font-black text-gray-900 mb-1" style={{ fontFamily: "'Syne', sans-serif" }}>
                      Get a Free Estimate
                    </h3>
                    <p className="text-gray-400 text-sm">No obligation · Response within 1 business day</p>
                  </div>

                  <form data-readdy-form id="get-free-quote-form" onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Full Name *</label>
                        <input type="text" name="name" required placeholder="John Smith"
                          className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-900 placeholder-gray-300 focus:outline-none focus:border-amber-400 transition-colors" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Phone *</label>
                        <input type="tel" name="phone" required placeholder="(925) 000-0000"
                          className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-900 placeholder-gray-300 focus:outline-none focus:border-amber-400 transition-colors" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Email *</label>
                      <input type="email" name="email" required placeholder="john@example.com"
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-900 placeholder-gray-300 focus:outline-none focus:border-amber-400 transition-colors" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Property Type</label>
                      <select name="property_type"
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-900 focus:outline-none focus:border-amber-400 transition-colors bg-white cursor-pointer">
                        <option value="">Select property type</option>
                        {propertyTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
                        Message <span className="text-gray-300 font-normal normal-case">({charCount}/500)</span>
                      </label>
                      <textarea name="message" rows={3} maxLength={500}
                        placeholder="Describe your issue or project — solar repair, battery, electrical, EV charger..."
                        onChange={(e) => setCharCount(e.target.value.length)}
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-900 placeholder-gray-300 focus:outline-none focus:border-amber-400 transition-colors resize-none">
                      </textarea>
                    </div>
                    <button type="submit" disabled={loading}
                      className="w-full bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white font-black py-4 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm tracking-wide">
                      {loading ? "Sending..." : "Get My Free Estimate →"}
                    </button>
                    <p className="text-center text-xs text-gray-400">
                      Or call: <a href="tel:9258723408" className="text-amber-500 font-semibold hover:underline">(925) 872-3408</a>
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
